import React, { useEffect, useRef } from 'react';
import Select from 'react-select';
import { Button, FileInput } from 'flowbite-react';
import { BsArrowLeft } from 'react-icons/bs';
import dayjs, { Dayjs } from 'dayjs';
import { ApplicationFormDataDetails } from 'types';
import DateSelect from './DateSelect';
import { sendTrackingEvent } from 'src/utils/gtm';
import { useLocation } from 'react-router-dom';

interface ApplicationFormDetailsProps {
	currentFormStep: number;
	applicationFormData: ApplicationFormDataDetails;
	applicationDetails: Array<{
		title: string;
		options?: { label: string; value: string; subValue?: string }[];
		isDate?: boolean;
		previousDependent?: boolean;
		previousSelectedOption?: Record<string, { label: string; value: string; subValue?: string }[]>;
		key: string;
		isMultiSelect?: boolean;
		isFileUpload?: boolean;
		dateFormat?: string;
	}>;
	handleOptionSelect: ({
		key,
		optionValue,
		changePage,
	}: {
		key: string;
		optionValue: string | string[] | Dayjs | Date | File | undefined;
		changePage?: boolean;
	}) => void;
	handlePreviousStep: () => void;
	handleSubmit?: () => void;
	loading?: boolean;
}

const ApplicationFormDetails: React.FC<ApplicationFormDetailsProps> = ({
	currentFormStep,
	applicationFormData,
	applicationDetails,
	handleOptionSelect,
	handlePreviousStep,
	handleSubmit,
}) => {
	const location = useLocation();
	const formStep = applicationDetails[currentFormStep - 1];
	const previousFormStep = applicationDetails[currentFormStep - 2];
	const selectedOption = formStep ? applicationFormData[formStep.key as keyof ApplicationFormDataDetails] : undefined;
	const previousSelectedOption = previousFormStep
		? applicationFormData[previousFormStep.key as keyof ApplicationFormDataDetails]
		: undefined;
	const selectRef = useRef<any>(null);

	if (!formStep) {
		return null;
	}

	const handleMultiSelect = (value: string) => {
		if (Array.isArray(selectedOption)) {
			const currentValues: string[] = selectedOption;
			const newValues = currentValues.includes(value)
				? currentValues.filter(v => v !== value)
				: [...currentValues, value];
			handleOptionSelect({
				key: formStep.key,
				optionValue: newValues,
				changePage: false,
			});
		}
	};

	return (
		<div className="lg:max-w-[438.75px] h-full flex flex-col pb-10 w-full">
			<h2 className="text-lg font-semibold font-poppins">Application form details</h2>
			<div className="w-full flex mt-[13px] md:gap-[14px] gap-[5px] max-h-[7.02px]">
				{applicationDetails.map((step, index) => (
					<div className="flex-1 w-full" key={index + step?.key}>
						<div
							className={`rounded-md ${selectedOption !== undefined && index <= currentFormStep - 1 ? 'bg-[#6FCD9E]' : 'bg-[#E5E5E5]'
								} py-1`}
						/>
					</div>
				))}
			</div>
			<div className="mt-[29px]">
				<h3 className="text-lg font-[500] font-openSans">
					{applicationDetails[currentFormStep - 1]?.title}
					{formStep.key === 'resume' ? "" : <span className="text-red-500"> *</span>}
				</h3>
				<div className="flex flex-col gap-5 lg:max-w-[374.4px] mx-auto mt-[18px]">
					{formStep.isDate ? (
						<DateSelect
							format={formStep?.dateFormat || 'DD/MMM/YYYY'}
							onClose={date => {
								if (date) {
									handleOptionSelect({
										key: formStep.key,
										optionValue: date?.toDate()?.toDateString() || '',
									});
								}
							}}
							restProps={{
								views: formStep?.dateFormat === 'YYYY' ? ['year'] : undefined,
								closeOnSelect: false,
								disableFuture: true,
								onYearChange(year) { },
								onSelectedSectionsChange(sections) { },
							}}
							value={selectedOption ? dayjs(selectedOption as string) : undefined}
						/>
					) : formStep.isFileUpload ? (
						<>
							<FileInput
								id="file"
								accept="image/png, application/pdf"
								helperText="Upload files upto 2MB only"
								className="text-xs font-normal italic"
								onChange={(e) => {
									handleOptionSelect({
										key: formStep.key,
										optionValue: e.target.files ? e.target.files[0] : undefined,
									});
									if (e.target.files) {
										handleOptionSelect({
											key: formStep.key,
											optionValue: e.target.files[0],
											changePage: true,
										});
									}
								}}
							/>
						</>
					) : formStep?.isMultiSelect ? (
						formStep?.options && formStep?.options?.length > 0 ? (
							<div ref={selectRef}>
								<Select
									isMulti
									menuPosition="fixed"
									placeholder="Select your degrees"
									className="flowbite-select--md"
									classNamePrefix="flowbite-select"
									options={formStep?.options}
									value={formStep?.options.filter(option => Array.isArray(selectedOption) && selectedOption?.includes(option?.value))}
									onChange={option => {
										handleOptionSelect({
											key: formStep.key,
											optionValue: option?.map((opt: any) => opt?.value),
											changePage: false,
										});
									}}
									closeMenuOnSelect={false}
									onMenuClose={() => {
										handleOptionSelect({
											key: formStep.key,
											optionValue: selectedOption ?? undefined,
											changePage: true,
										});
									}}
								/>
							</div>
						) : (
							<p>Loading options...</p>
						)
					) : formStep?.previousDependent ? (
						previousSelectedOption &&
						formStep?.previousSelectedOption?.[
							previousSelectedOption as keyof Record<string, { label: string; value: string; subValue?: string }[]>
						]?.map((option, index) => (
							<Button
								key={index}
								size="lg"
								color={selectedOption === option.value ? 'purplePlaced' : 'gray'}
								onClick={() =>
									handleOptionSelect({
										key: formStep.key,
										optionValue: option.value,
									})
								}
							>
								<div className="flex flex-col">
									<div className="font-medium no-underline text-left">{option.label}</div>
									{option.subValue && <div className="text-sm font-normal text-left">{option.subValue}</div>}
								</div>
							</Button>
						))
					) : (
						formStep?.options?.map((option, index) => (
							<Button
								key={index}
								size="lg"
								color={selectedOption === option.value ? 'purplePlaced' : 'gray'}
								onClick={() =>
									handleOptionSelect({
										key: formStep.key,
										optionValue: option.value,
									})
								}
							>
								<div className="flex flex-col">
									<div className="font-medium no-underline text-left">{option.label}</div>
									{option.subValue && <div className="text-sm font-normal text-left">{option.subValue}</div>}
								</div>
							</Button>
						))
					)}
				</div>
			</div>

			<div className="mt-auto pt-6 flex items-center justify-between gap-2">
				{currentFormStep === 1 ? null : (
					<Button onClick={handlePreviousStep} color="gray" className="w-[42px] font-inter" size="lg">
						<BsArrowLeft />
					</Button>
				)}
				{currentFormStep === applicationDetails.length && (
					<Button
						size="md"
						disabled={
							formStep?.key === 'resume'
								? false
								: formStep?.isMultiSelect
									? !Array.isArray(selectedOption) || !selectedOption.length
									: !selectedOption
						}
						onClick={() => {
							handleSubmit?.();
							sendTrackingEvent({
								event: 'recr_dashboard_active_companies',
								eventLabel: location.pathname,
							});
						}}
						color="purplePlaced"
						className="w-full flex-1"
					>
						Submit
					</Button>
				)}
			</div>
		</div>
	);
};

export default ApplicationFormDetails;